import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'

import Footer from '@/components/footer'
import Header from '@/components/header'
import SEO from '@/components/seo'
import type {HomepageV3Query} from '@/graphql-types'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import {Main} from '@helios/shared/components/skip-to-content'
import locales from '@helios/shared/locale/locales'
import {useLocale} from '@helios/shared/providers/locale'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'

import {SliceZone} from './slice-zone'

type Props = PageProps<HomepageV3Query>

export const HomepageV3 = (props: Props) => {
    const data = props.data.prismicHomepageV3.data
    const currentLocale = useLocale()

    if (!data) {
        return handleNoPrismicData()
    }

    return (
        <>
            <Header
                hideLeftNav={currentLocale.isPreLaunch || data.hide_left_nav}
                hideRightNav={currentLocale.isPreLaunch || data.hide_right_nav}
            />

            <Main>
                <SliceZone slices={data.body} trackVisibleSections={data.track_visible_sections} />
            </Main>

            <Footer hideLinks={data.hide_links} />
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<HomepageV3Query>) => {
    const pageData = data.prismicHomepageV3.data

    // Can't access React Context here (until gatsby@5.6.0), so useLocale() doesn't work
    const locale = locales.find((locale) => {
        return locale.code === pageContext.locale
    })

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            preventIndexing={pageData.exclude_robots}
            canonicalLocation={`/${locale?.urlCode}`}
        />
    )
}

export const query = graphql`
    query HomepageV3(
        $locale: String!
        $uid: String!
        $imageBreakpoints: [Int]
        $twoColumnsAsymmetric: String
    ) {
        prismicHomepageV3(uid: {eq: $uid}, lang: {eq: $locale}) {
            _previewable
            data {
                exclude_robots
                hide_left_nav
                hide_right_nav
                hide_links
                track_visible_sections
                body {
                    ...FeatureBlock
                    ...GetStarted
                    ...Statistics
                    ...CustomerLogosPlusCta
                    ...RotatingBanner
                    ...VideoTabs
                    ...VideoWithOutline
                    ...Integrations
                    ...Hero
                    ...VideoHero
                    ...CardGrid
                    ...ContentPlusBlocks
                    ...StepsSection
                    ...RatingsPlusHeadline
                    ...SocialProof
                    ...FeatureGrid
                }
            }
        }
    }

    fragment Integrations on PrismicHomepageV3DataBodyIntegrations {
        __typename
        primary {
            title
            cta_text
            cta_link {
                ...PrismicLink
            }
            more_text
            logo_1 {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            logo_1_link {
                ...PrismicLink
            }
            logo_2 {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            logo_2_link {
                ...PrismicLink
            }
            logo_3 {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            logo_3_link {
                ...PrismicLink
            }
        }
    }

    fragment CustomerLogosPlusCta on PrismicHomepageV3DataBodyCustomerLogosPlusCta {
        __typename
        primary {
            title
            cta_text
            cta_link {
                ...PrismicLink
            }
            marquee_mode
            background_color
        }
        items {
            logo {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
        }
    }

    fragment CardGrid on PrismicHomepageV3DataBodyCardGrid {
        __typename
        primary {
            blockTitle: title {
                richText
            }
            subtitle
            show_more_text
            initial_cards_shown
            initial_cards_shown_on_mobile
            initial_cards_shown_on_desktop
            card_cta_text
            show_filter
            layout
        }
        items {
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            title
            body
            ctaLink: cta_link {
                ...PrismicLink
            }
            filterKeywords: filter_keywords
            tag_text
            tag_color
        }
    }

    fragment VideoTabs on PrismicHomepageV3DataBodyVideoTabs {
        __typename
        items {
            icon {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            title
            description
            poster {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            video_mp4 {
                ...Video
            }
            video_webm {
                ...Video
            }
        }
    }

    fragment VideoWithOutline on PrismicHomepageV3DataBodyVideoWithOutline {
        __typename
        primary {
            title
        }
        items {
            description
            name_title
            video_id
            thumbnail {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(
                    breakpoints: $imageBreakpoints
                    sizes: "(max-width: 991px) calc(100vw - 48px), (max-width: 1439px) calc(33vw - 220px), 367px"
                )
            }
            customer_logo {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
        }
    }

    fragment RotatingBanner on PrismicHomepageV3DataBodyRotatingBanner {
        __typename
        items {
            text
        }
        primary {
            cta_text
            cta_link {
                ...PrismicLink
            }
            size_variant
            bg_color
        }
    }

    fragment SignupReason on PrismicSignupReason {
        data {
            primary
            secondary
            icon {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
        }
    }

    fragment Hero on PrismicHomepageV3DataBodyHero {
        __typename
        primary {
            section_html_id
            title
            inverse
            lead_text {
                richText
            }
            submit_label
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $twoColumnsAsymmetric)
            }
            wistia_video_id
            rotating_signup_reasons
            signup_reason_1 {
                document {
                    ...SignupReason
                }
            }
            signup_reason_2 {
                document {
                    ...SignupReason
                }
            }
            signup_reason_3 {
                document {
                    ...SignupReason
                }
            }
            video_mp4 {
                ...Video
            }
            video_webm {
                ...Video
            }
            predefined_signup_banner {
                document {
                    ...SignupBanner
                }
            }
        }
    }

    fragment VideoHero on PrismicHomepageV3DataBodyVideoHero {
        __typename
        primary {
            title
            lead_text {
                richText
            }
            signup_label
            predefined_signup_banner {
                document {
                    ...SignupBanner
                }
            }
            cta_text
            cta_link {
                ...PrismicLink
            }
            signup_modal_image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(
                    breakpoints: $imageBreakpoints
                    sizes: "(max-width: 767px) 40px, 60px"
                )
            }
            video_mp4 {
                ...Video
            }
            video_webm {
                ...Video
            }
            video_poster {
                url
            }
        }
    }

    fragment Statistics on PrismicHomepageV3DataBodyStats {
        __typename
        primary {
            first_stat_amount
            first_stat_text
            first_stat_as_percentile
            second_stat_amount
            second_stat_text
            second_stat_as_percentile
            third_stat_amount
            third_stat_text
            third_stat_as_percentile
            source_text {
                richText
            }
            predefined_statistics {
                document {
                    ... on PrismicStatisticsSection {
                        data {
                            first_stat {
                                amount
                                text
                                as_percentile
                            }
                            second_stat {
                                amount
                                text
                                as_percentile
                            }
                            third_stat {
                                amount
                                text
                                as_percentile
                            }
                            source_text {
                                richText
                            }
                        }
                    }
                }
            }
        }
    }

    fragment GetStarted on PrismicHomepageV3DataBodyGetStarted {
        __typename
        primary {
            title
            error_message
            button_label
            input_placeholder
            form_subtitle
            aria_label
            hubspot_form_id_staging
            hubspot_form_id_production
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            predefined_signup_banner {
                document {
                    ...SignupBanner
                }
            }
        }
    }

    fragment FeatureBlock on PrismicHomepageV3DataBodyFeatureBlock {
        __typename
        primary {
            title_over_section
            title
            inverse
            background
            headline
            description {
                richText
            }
            pre_cta_links {
                richText
            }
            cta_label
            is_circular_image
            predefined_feature_block {
                document {
                    ... on PrismicFeatureBlock {
                        data {
                            image {
                                alt
                                copyright
                                url
                                dimensions {
                                    height
                                    width
                                }
                                gatsbyImageData(
                                    breakpoints: $imageBreakpoints
                                    sizes: $twoColumnsAsymmetric
                                )
                            }
                            title
                            subtitle
                            body {
                                richText
                            }
                        }
                    }
                }
            }
            cta_link {
                ...PrismicLink
            }
            cta_hash_link
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $twoColumnsAsymmetric)
            }
            wistia_video_id
            video_mp4 {
                ...Video
            }
            video_webm {
                ...Video
            }
        }
    }

    fragment ContentPlusBlocks on PrismicHomepageV3DataBodyContentPlusBlocks {
        __typename
        primary {
            background
            plainTitle: title
            text_under_title {
                richText
            }
        }
        items {
            block_image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            block_title
            block_content {
                richText
            }
        }
    }

    fragment SocialProof on PrismicHomepageV3DataBodySocialProof {
        __typename
        primary {
            inverse
            title
        }
        items {
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $twoColumnsAsymmetric)
            }
            quote
            full_name
            person_byline
            predefined_testimonial {
                document {
                    ... on PrismicTestimonial {
                        data {
                            image {
                                alt
                                copyright
                                url
                                dimensions {
                                    height
                                    width
                                }
                                gatsbyImageData(
                                    breakpoints: $imageBreakpoints
                                    sizes: $twoColumnsAsymmetric
                                )
                            }
                            quote
                            attribution_name
                            attribution_title
                        }
                    }
                }
            }
        }
    }

    fragment FeatureGrid on PrismicHomepageV3DataBodyFeatureGrid {
        __typename
        primary {
            title
            background
        }
        items {
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $twoColumnsAsymmetric)
            }
            feature_title
            feature_description {
                richText
            }
            predefined_feature {
                document {
                    ... on PrismicFeatureIconCard {
                        data {
                            icon {
                                alt
                                copyright
                                url
                                dimensions {
                                    height
                                    width
                                }
                                gatsbyImageData(
                                    breakpoints: $imageBreakpoints
                                    sizes: $twoColumnsAsymmetric
                                )
                            }
                            title
                            body
                        }
                    }
                }
            }
        }
    }

    fragment StepsSection on PrismicHomepageV3DataBodySteps {
        __typename
        primary {
            title
            show_count
            description {
                richText
            }
            cta_link {
                ...PrismicLink
            }
            cta_text
        }
        items {
            heading
            content
            background_color
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(
                    breakpoints: $imageBreakpoints
                    sizes: "(max-width: 767px) calc(100vw - 48px), (max-width: 1200px) calc(100vw / 2 - 48px), (max-width: 1439px) calc(100vw / 2 - 120px), 600px"
                )
            }
        }
    }

    fragment RatingsPlusHeadline on PrismicHomepageV3DataBodyRatingsPlusHeadline {
        __typename
        primary {
            ratings_title {
                richText
            }
            ratings_lead_text
            ratings_cta_text
            cta_link {
                ...PrismicLink
            }
            predefined_ratings {
                document {
                    ... on PrismicSocialProofRatingsSection {
                        data {
                            title {
                                richText
                            }
                            lead_text
                            cta_text
                            cta_link {
                                ...PrismicLink
                            }
                            ratings {
                                rating {
                                    document {
                                        ... on PrismicRating {
                                            data {
                                                logo {
                                                    alt
                                                    copyright
                                                    url
                                                    dimensions {
                                                        height
                                                        width
                                                    }
                                                }
                                                stars
                                                rating_number
                                                link
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        items {
            rating {
                document {
                    ... on PrismicRating {
                        data {
                            logo {
                                alt
                                copyright
                                url
                                dimensions {
                                    height
                                    width
                                }
                            }
                            stars
                            rating_number
                            link
                        }
                    }
                }
            }
        }
    }
`

export default withPrismicPreview(HomepageV3)
